/**
 * Shared visual primitives for rendering disposition completeness status:
 *   - SectionScoreBadge: "X/Y captured" pill (green/amber/red) shown next to
 *     section accordion triggers
 *   - DispositionFieldIcon: per-field status dot (green check / red alert / muted)
 *
 * Used both by FieldGroupAccordion (top-level scalar fields) and by the drug/
 * procedure section components rendered inside DrugOutputField/ProcedureOutputField.
 */

import { AlertCircle, CheckCircle, Circle } from 'lucide-react';
import React from 'react';

import type { DispositionFieldStatus, SectionScore } from '@/utils/dispositionCompleteness';

interface SectionScoreBadgeProps {
  score: SectionScore | undefined;
}

/**
 * Renders a filled/total badge for a disposition section's completeness score.
 * Returns null when no score is available.
 */
export const SectionScoreBadge: React.FC<SectionScoreBadgeProps> = ({ score }) => {
  if (!score) return null;
  const colorClass =
    score.filled === score.total
      ? 'text-positive-foreground'
      : score.filled > 0
        ? 'text-attention-foreground'
        : 'text-destructive';
  return (
    <span className={`text-xs font-medium ${colorClass}`}>
      {score.filled}/{score.total} captured
    </span>
  );
};

interface DispositionFieldIconProps {
  status?: DispositionFieldStatus;
}

/**
 * Renders the disposition status icon for a field. Muted dot when the field
 * isn't part of the required set.
 */
export const DispositionFieldIcon: React.FC<DispositionFieldIconProps> = ({ status }) => {
  if (!status) return <Circle className="w-3.5 h-3.5 text-muted-foreground/40 shrink-0" />;
  if (status === 'filled')
    return <CheckCircle className="w-3.5 h-3.5 text-positive-foreground shrink-0" />;
  return <AlertCircle className="w-3.5 h-3.5 text-destructive shrink-0" />;
};
